import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { eggState, announceEgg } from '../../lib/eggs';
import { HALL_WINDOWS } from './HallWindows';
import { groundHeight } from './Landscape';

// The 3D side of the Easter eggs (see lib/eggs.js): confetti from the glass roof (Konami code),
// fireworks over the mountains, birthday balloons drifting past the windows, the artist's
// signature revealed by lightning, and a rainbow after heavy rain. Festivals (Festivals.jsx) reuse
// the confetti, fireworks and balloons in their own colours. Every effect exists from the start but draws nothing until it's
// needed (count / drawRange 0), so the shader prewarm covers it and nothing compiles mid-visit.

const IS_MOBILE = typeof window !== 'undefined' && (window.innerWidth < 768 || /iPhone|iPad|Android/i.test(navigator.userAgent));
const rand = (a, b) => a + Math.random() * (b - a);

const glowTexture = () => {
  const c = document.createElement('canvas'); c.width = c.height = 32;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.35, 'rgba(255,255,255,0.7)'); g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, 32, 32);
  return new THREE.CanvasTexture(c);
};

// ------------------------------------------------------------------ confetti (Konami code)
const CONFETTI_COLORS = ['#e63946', '#f4a261', '#2a9d8f', '#457b9d', '#e9c46a', '#9b5de5', '#f15bb5', '#ffffff'].map((c) => new THREE.Color(c));
const Confetti = () => {
  const n = IS_MOBILE ? 220 : 450;
  const { mesh, bits } = useMemo(() => {
    const m = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.09, 0.055), new THREE.MeshBasicMaterial({ side: THREE.DoubleSide }), n);
    m.frustumCulled = false;
    m.count = 0;
    const b = Array.from({ length: n }, (_, i) => {
      m.setColorAt(i, CONFETTI_COLORS[i % CONFETTI_COLORS.length]);
      return { x: 0, y: -1, z: 0, vy: 0, ph: Math.random() * 6, spin: new THREE.Vector3(rand(2, 6), rand(2, 6), rand(1, 4)), rot: new THREE.Euler() };
    });
    return { mesh: m, bits: b };
  }, [n]);
  useEffect(() => () => { mesh.geometry.dispose(); mesh.material.dispose(); mesh.dispose(); }, [mesh]);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const wasOn = useRef(false);
  useFrame((state, delta) => {
    const now = performance.now();
    const on = now < Math.max(eggState.konamiUntil, eggState.confettiUntil);
    if (on && !wasOn.current) {
      // festivals bring their own colours (petals, leaves, snow…) and drift down more slowly
      const festive = now < eggState.confettiUntil && eggState.confettiColors;
      const colors = festive ? eggState.confettiColors : CONFETTI_COLORS;
      const slow = festive ? 0.45 : 1;
      bits.forEach((c, i) => { c.x = rand(-10, 10); c.z = rand(-10, 10); c.y = rand(7.6, 14); c.vy = rand(0.8, 1.5) * slow; mesh.setColorAt(i, colors[i % colors.length]); });
      mesh.instanceColor.needsUpdate = true;
    }
    wasOn.current = on;
    const anyUp = bits.some((c) => c.y > 0);
    if (!on && !anyUp) { mesh.count = 0; return; }
    mesh.count = n;
    const dt = Math.min(delta, 0.05), t = state.clock.elapsedTime;
    bits.forEach((c, i) => {
      if (c.y > 0) {
        c.y -= c.vy * dt;
        c.x += Math.sin(t * 2 + c.ph) * 0.6 * dt;
        c.z += Math.cos(t * 1.7 + c.ph) * 0.6 * dt;
        if (c.y <= 0.02 && on) { c.y = rand(7.6, 9); } // keep falling while it lasts
        c.rot.x += c.spin.x * dt; c.rot.y += c.spin.y * dt; c.rot.z += c.spin.z * dt;
      }
      dummy.position.set(c.x, Math.max(c.y, 0.02), c.z);
      dummy.rotation.copy(c.rot);
      dummy.scale.setScalar(c.y > 0 ? 1 : 0);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
  });
  return <primitive object={mesh} />;
};

// ------------------------------------------------------------------ fireworks
const FIREWORK_COLORS = ['#ff4d6d', '#ffd166', '#06d6a0', '#4cc9f0', '#f72585', '#ffffff', '#ff9e00'].map((c) => new THREE.Color(c));
const Fireworks = () => {
  const PER = IS_MOBILE ? 70 : 120, BURSTS = 8, n = PER * BURSTS;
  const data = useMemo(() => {
    const pos = new Float32Array(n * 3), col = new Float32Array(n * 3);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setDrawRange(0, 0);
    const tex = glowTexture();
    const mat = new THREE.PointsMaterial({ size: 4.5, map: tex, vertexColors: true, transparent: false, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
    const points = new THREE.Points(g, mat);
    points.frustumCulled = false;
    const sparks = Array.from({ length: n }, () => ({ x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, age: 99, life: 2, c: FIREWORK_COLORS[0] }));
    return { g, mat, tex, points, sparks, next: 0, slot: 0, lastBurst: 0 };
  }, [n]);
  useEffect(() => () => { data.g.dispose(); data.mat.dispose(); data.tex.dispose(); }, [data]);
  useFrame((state, delta) => {
    const now = performance.now();
    const on = now < eggState.fireworksUntil;
    const dt = Math.min(delta, 0.05);
    if (on && now > data.next) {
      // a new burst high above the mountains, mostly to the north (seen through the windows and the roof)
      data.next = now + rand(500, 1100);
      const a = rand(-2.4, -0.7), r = rand(110, 170);
      const cx = Math.cos(a) * r, cz = Math.sin(a) * r, cy = rand(55, 105);
      const palette = eggState.fireworksColors || FIREWORK_COLORS;
      const color = palette[(Math.random() * palette.length) | 0];
      const start = data.slot * PER;
      data.slot = (data.slot + 1) % BURSTS;
      for (let i = 0; i < PER; i++) {
        const s = data.sparks[start + i];
        const u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2, sp = rand(16, 24);
        const k = Math.sqrt(1 - u * u);
        Object.assign(s, { x: cx, y: cy, z: cz, vx: Math.cos(th) * k * sp, vy: u * sp, vz: Math.sin(th) * k * sp, age: 0, life: rand(1.6, 2.6), c: Math.random() < 0.15 ? FIREWORK_COLORS[5] : color });
      }
    }
    // a small gold burst where a shooting star was caught (see NightSky.jsx)
    if (eggState.burst && eggState.burst.at !== data.lastBurst) {
      data.lastBurst = eggState.burst.at;
      const b = eggState.burst, start = data.slot * PER;
      data.slot = (data.slot + 1) % BURSTS;
      for (let i = 0; i < PER; i++) {
        const s = data.sparks[start + i];
        const u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2, sp = rand(5, 12), k = Math.sqrt(1 - u * u);
        Object.assign(s, { x: b.x, y: b.y, z: b.z, vx: Math.cos(th) * k * sp, vy: u * sp, vz: Math.sin(th) * k * sp, age: 0, life: rand(1, 1.8), c: i % 3 ? FIREWORK_COLORS[1] : FIREWORK_COLORS[5] });
      }
    }
    const pos = data.g.attributes.position.array, col = data.g.attributes.color.array;
    let alive = false;
    data.sparks.forEach((s, i) => {
      s.age += dt;
      if (s.age < s.life) {
        alive = true;
        s.vx *= 1 - 1.2 * dt; s.vy = s.vy * (1 - 1.2 * dt) - 7 * dt; s.vz *= 1 - 1.2 * dt;
        s.x += s.vx * dt; s.y += s.vy * dt; s.z += s.vz * dt;
      }
      const b = s.age < s.life ? Math.pow(1 - s.age / s.life, 1.5) * (0.8 + 0.2 * Math.sin(s.age * 40 + i)) : 0;
      pos[i * 3] = s.x; pos[i * 3 + 1] = s.y; pos[i * 3 + 2] = s.z;
      col[i * 3] = s.c.r * b * 1.6; col[i * 3 + 1] = s.c.g * b * 1.6; col[i * 3 + 2] = s.c.b * b * 1.6;
    });
    data.g.setDrawRange(0, alive ? n : 0);
    if (alive) { data.g.attributes.position.needsUpdate = true; data.g.attributes.color.needsUpdate = true; }
  });
  return <primitive object={data.points} />;
};

// ------------------------------------------------------------------ birthday balloons
const BALLOON_COLORS = ['#e63946', '#ffb703', '#219ebc', '#8338ec', '#fb5607', '#06d6a0', '#ff70a6'].map((c) => new THREE.Color(c));
const SPOTS = [
  ...HALL_WINDOWS.north.map((x) => ({ x, z: -12, nx: 0, nz: -1 })),
  ...HALL_WINDOWS.east.map((x) => ({ x: 12, z: x, nx: 1, nz: 0 })),
  ...HALL_WINDOWS.west.map((x) => ({ x: -12, z: -x, nx: -1, nz: 0 })),
];
const makeBalloonGeometry = () => {
  const ball = new THREE.SphereGeometry(0.32, 16, 12);
  ball.scale(1, 1.18, 1);
  ball.translate(0, 0.4, 0);
  const knot = new THREE.ConeGeometry(0.05, 0.08, 8);
  knot.translate(0, 0.03, 0);
  const string = new THREE.CylinderGeometry(0.004, 0.004, 1.3, 3, 1, true);
  string.translate(0, -0.65, 0);
  const parts = [ball, knot, string].map((g) => (g.index ? g.toNonIndexed() : g));
  const pos = [], nor = [];
  parts.forEach((g) => { pos.push(...g.attributes.position.array); nor.push(...g.attributes.normal.array); g.dispose(); });
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  return out;
};
const Balloons = () => {
  const n = IS_MOBILE ? 20 : 36;
  const { mesh, items } = useMemo(() => {
    const m = new THREE.InstancedMesh(makeBalloonGeometry(), new THREE.MeshStandardMaterial({ roughness: 0.3, metalness: 0.05 }), n);
    m.frustumCulled = false;
    m.count = 0;
    const it = Array.from({ length: n }, (_, i) => {
      m.setColorAt(i, BALLOON_COLORS[i % BALLOON_COLORS.length]);
      return { spot: SPOTS[i % SPOTS.length], x: 0, y: -100, z: 0, speed: rand(0.6, 1.1), ph: Math.random() * 6 };
    });
    return { mesh: m, items: it };
  }, [n]);
  useEffect(() => () => { mesh.geometry.dispose(); mesh.material.dispose(); mesh.dispose(); }, [mesh]);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const respawn = (b, first) => {
    const out = rand(3, 13), lat = rand(-4, 4);
    b.x = b.spot.x + b.spot.nx * out - b.spot.nz * lat;
    b.z = b.spot.z + b.spot.nz * out + b.spot.nx * lat;
    b.y = groundHeight(b.x, b.z) + (first ? rand(0, 18) : 0);
  };
  useFrame((state, delta) => {
    if (!eggState.celebrate) { mesh.count = 0; return; }
    if (mesh.count === 0) {
      const colors = eggState.balloonColors || BALLOON_COLORS;
      items.forEach((b, i) => { respawn(b, true); mesh.setColorAt(i, colors[i % colors.length]); });
      mesh.instanceColor.needsUpdate = true;
    }
    mesh.count = n;
    const dt = Math.min(delta, 0.05), t = state.clock.elapsedTime;
    items.forEach((b, i) => {
      b.y += b.speed * dt;
      b.x += Math.sin(t * 0.5 + b.ph) * 0.3 * dt;
      if (b.y > 34) respawn(b, false);
      dummy.position.set(b.x, b.y, b.z);
      dummy.rotation.set(Math.sin(t * 0.9 + b.ph) * 0.12, 0, Math.cos(t * 0.7 + b.ph) * 0.12);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
  });
  return <primitive object={mesh} />;
};

// ------------------------------------------------------------------ signature revealed by lightning
const makeSignatureTexture = () => {
  const c = document.createElement('canvas'); c.width = 512; c.height = 128;
  const ctx = c.getContext('2d');
  ctx.font = 'italic 64px "Brush Script MT", "Segoe Script", cursive';
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(180,220,255,1)';
  ctx.shadowBlur = 18;
  ctx.fillText('David Raj Paul', 256, 64);
  return new THREE.CanvasTexture(c);
};
const StormSignature = ({ weatherRef, paintings, wallHeight }) => {
  const meshRef = useRef();
  const tex = useMemo(makeSignatureTexture, []);
  const mat = useMemo(() => new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, color: '#cfe6ff', toneMapped: false }), [tex]);
  useEffect(() => () => { tex.dispose(); mat.dispose(); }, [tex, mat]);
  const last = useRef({ strikes: -1, told: false });
  useFrame(() => {
    const w = weatherRef.current, mesh = meshRef.current;
    if (!mesh) return;
    const strength = (w.flash || 0) * (w.storm || 0);
    mat.opacity = Math.min(1, strength * 1.4);
    if (w.strikes !== last.current.strikes) {
      // each strike reveals the signature on a different painting, in its lower right corner
      last.current.strikes = w.strikes;
      const p = paintings[(w.strikes || 0) % paintings.length];
      const e = new THREE.Euler(p.rotation.x, p.rotation.y, p.rotation.z);
      const right = new THREE.Vector3(1, 0, 0).applyEuler(e), up = new THREE.Vector3(0, 1, 0), fwd = new THREE.Vector3(0, 0, 1).applyEuler(e);
      const c = new THREE.Vector3(p.position.x, p.centerY ?? wallHeight / 2, p.position.z);
      mesh.position.copy(c).addScaledVector(right, p.size.width / 2 - 0.5).addScaledVector(up, -p.size.height / 2 + 0.16).addScaledVector(fwd, 0.05);
      mesh.rotation.copy(e);
    }
    if ((w.storm || 0) > 0.5 && w.strikes > 0 && !last.current.told) {
      last.current.told = true;
      setTimeout(() => announceEgg('signature', "The lightning revealed the artist's signature on a painting.", '⚡'), 600);
    }
  });
  return (
    <mesh ref={meshRef} material={mat} renderOrder={3} position={[0, -50, 0]}>
      <planeGeometry args={[0.8, 0.2]} />
    </mesh>
  );
};

// ------------------------------------------------------------------ rainbow after the rain
// When heavy rain clears into a bright day, a rainbow arcs over the mountains for a minute or so.
// A wide ring segment far away, coloured in the shader by how far across the band each pixel is.
// Opaque + additive like the aurora (shows through the glass roof), drawRange 0 when it's gone.
const rainbowVertex = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const rainbowFragment = /* glsl */ `
  uniform float uStrength;
  varying vec2 vUv;
  vec3 spectrum(float x) { // x: 0 inside (violet) … 1 outside (red)
    return clamp(vec3(abs(x * 6.0 - 4.0) - 1.0, 2.0 - abs(x * 6.0 - 2.5), 2.0 - abs(x * 6.0 - 1.0)) * vec3(1.0, 0.9, 1.0), 0.0, 1.0);
  }
  void main() {
    float x = vUv.y;                                   // across the band
    float edge = smoothstep(0.0, 0.18, x) * smoothstep(1.0, 0.82, x);
    float ends = smoothstep(0.0, 0.12, vUv.x) * smoothstep(1.0, 0.88, vUv.x); // fade into the hills
    gl_FragColor = vec4(spectrum(x) * edge * ends * uStrength * 0.9, 1.0);
  }
`;
const Rainbow = ({ weatherRef, nightRef }) => {
  const { geometry, material } = useMemo(() => {
    // RingGeometry's uv isn't radial, so build the arc by hand: u along the arc, v across it
    const SEG = 96, R = 210, W = 28, pos = [], uv = [], idx = [];
    for (let i = 0; i <= SEG; i++) {
      const a = Math.PI * (0.02 + 0.96 * (i / SEG));
      for (let j = 0; j < 2; j++) {
        const r = R + j * W;
        pos.push(Math.cos(a) * r, Math.sin(a) * r * 0.62, 0);
        uv.push(i / SEG, j);
      }
      if (i < SEG) { const k = i * 2; idx.push(k, k + 2, k + 1, k + 1, k + 2, k + 3); }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setIndex(idx);
    geo.setDrawRange(0, 0);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uStrength: { value: 0 } }, vertexShader: rainbowVertex, fragmentShader: rainbowFragment,
      transparent: false, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false,
    });
    return { geometry: geo, material: mat };
  }, []);
  useEffect(() => () => { geometry.dispose(); material.dispose(); }, [geometry, material]);
  const st = useRef({ wet: 0, show: 0, level: 0, told: false, preview: typeof window !== 'undefined' && /[?&]rainbow\b/.test(window.location.search) });
  useFrame((_, delta) => {
    const w = weatherRef.current, s = st.current, dt = Math.min(delta, 0.1);
    const raining = Math.max(w.rain || 0, w.storm || 0);
    if (raining > 0.6) s.wet = 120;              // remember heavy rain for two minutes
    else s.wet = Math.max(0, s.wet - dt);
    const bright = (nightRef ? nightRef.current : 0) < 0.3 && (w.cloud || 0) < 0.55 && raining < 0.15;
    if ((s.wet > 0 || s.preview) && bright && s.show <= 0) { s.show = 70; s.wet = 0; s.preview = false; }
    if (s.show > 0) s.show -= dt;
    const target = s.show > 0 && bright ? 1 : 0;
    s.level += (target - s.level) * Math.min(1, dt * 0.25);
    material.uniforms.uStrength.value = s.level;
    geometry.setDrawRange(0, s.level > 0.01 ? geometry.index.count : 0);
    if (s.level > 0.5 && !s.told) {
      s.told = true;
      announceEgg('rainbow', 'The rain has cleared, and there is a rainbow over the mountains.', '🌈');
    }
  });
  // to the north, over the mountains, facing the hall (seen through the north windows and the roof)
  return <mesh geometry={geometry} material={material} position={[80, -10, -340]} rotation={[0, -0.23, 0]} frustumCulled={false} renderOrder={1} />;
};

export const EasterEggs = ({ weatherRef, nightRef, paintings, wallHeight }) => (
  <group>
    <Confetti />
    <Fireworks />
    <Balloons />
    <Rainbow weatherRef={weatherRef} nightRef={nightRef} />
    <StormSignature weatherRef={weatherRef} paintings={paintings} wallHeight={wallHeight} />
  </group>
);

export default EasterEggs;
